import { FunctionComponent, useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, OutlinedInput, FormHelperText, Autocomplete, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import LocationService from '../../../shared/services/location/location.service';
import { ICountry } from '../../../shared/services/location/location.model';
import { IPostEnergySupplierTariff } from '../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.model';
import EnergySupplierTariffService from '../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.service';

export const EnergySupplierTariffDialog: FunctionComponent<{
  energySupplierTariffId?: string,
  energySupplierId?: string,
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  isSubmitting: boolean,
  submitHandler: (newTariff: IPostEnergySupplierTariff, isEdit: boolean, resetForm: () => void, energySupplierTariffId?: string) => void,
}> = ({
  energySupplierTariffId,
  open,
  closeHandler,
  isEdit,
  isSubmitting,
  submitHandler,
  energySupplierId,
}) => {
    //Services
    const energySupplierTariffService = new EnergySupplierTariffService()
    const locationService = LocationService.getInstance()
    //State
    const [countries, setCountries] = useState<ICountry[]>()

    useEffect(() => {
      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])


    const tariffSchema = Yup.object().shape({
      energySupplierId: Yup.string().required(`You must provide an energy supplier for the tariff`),
      name: Yup.string().required(`You must provide a name for the tariff`),
      countryId: Yup.string().nullable(),
      certificateScheme: Yup.string().nullable(),
    })

    const formik = useFormik<IPostEnergySupplierTariff>({
      initialValues: {
        energySupplierId: energySupplierId!,
        name: '',
        countryId: '',
        certificateScheme: '',
        active: false,
      },
      validationSchema: tariffSchema,
      onSubmit: (values, { resetForm }) => {
        if (values.countryId === '') {
          values.countryId = null as any
        }
        submitHandler(values, isEdit, resetForm, energySupplierTariffId)
      }
    })

    useEffect(() => {
      if (open && isEdit && energySupplierTariffId) {
        energySupplierTariffService.getOne(energySupplierTariffId)
          .then(response => {
            formik.setValues({
              energySupplierId: response.energySupplierId,
              name: response.name,
              countryId: response.countryId,
              certificateScheme: response.certificateScheme,
              active: response.active,
            })
          })
          .catch(err => console.log(err))
      }
    }, [isEdit, energySupplierTariffId])

    const selectCountryHandler = (_e: any, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      } else {
        formik.setValues({ ...formik.values, countryId: '' })
      }
    }

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title={isEdit ? 'Edit Tariff' : 'Add Tariff'}
        cancelHandler={closeHandler}
        submitHandler={formik.submitForm}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
      >
        <Box component='form' onSubmit={formik.handleSubmit}>
          <FormControl fullWidth error={formik.touched.name && Boolean(formik.errors.name)} sx={{ mt: 2 }}>
            <InputLabel htmlFor='tariff-name'>Name</InputLabel>
            <OutlinedInput
              id='tariff-name'
              name='name'
              label='Name'
              value={formik.values.name || ''}
              onChange={formik.handleChange}
            />
            {formik.touched.name && formik.errors.name && <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.touched.name && formik.errors.name ? String(formik.errors.name) : ' '}</FormHelperText>
            }
          </FormControl>
          {countries && (
            <FormControl fullWidth error={formik.touched.countryId && Boolean(formik.errors.countryId)} sx={{ mt: 2 }}>
              <Autocomplete
                value={countries.find(country => country.id === formik.values.countryId) || null}
                onChange={(e, value) => selectCountryHandler(e, value)}
                options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
                getOptionLabel={(option) => option?.name ?? ''}
                renderInput={(params) => (
                  <TextField {...params} label='Country' variant='outlined' />
                )}
              />
              {formik.touched.countryId && formik.errors.countryId && <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.touched.countryId && formik.errors.countryId ? String(formik.errors.countryId) : ' '}</FormHelperText>
              }
            </FormControl>
          )}
        </Box>
      </DialogWrapper>
    )
  }


